/**
 * MyDayDashboard
 *
 * Workstream B — Provider's "My Day" landing page.
 *
 * Replaces ProviderDashboard.tsx for the practitioner portal.
 * Answers: "What do I need to do right now?"
 *
 * Layout:
 *   Left column (60%): Today's Schedule — chronological appointment list
 *   Right column (40%): Pending Rx, IH Alerts, Unread Messages, Overdue Tasks
 *
 * All data comes from existing services. No new API endpoints needed.
 *
 * PRD Reference: Section 4A — 'My Day' Dashboard
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarDays,
  Pill,
  Sparkles,
  MessageSquare,
  ClipboardList,
  Loader2,
  ArrowRight,
  Clock,
  AlertTriangle,
  User,
  Video,
  MapPin,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { emrService, EmrAppointment } from '@/services/emrService';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { TaskPanel } from '@/components/tasks';
import { cn, toTitleCase } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface DayMetrics {
  todayAppointments: number;
  pendingRx: number;
  intelliHealthAlerts: number;
  unreadMessages: number;
  overdueTasks: number;
}

// ============================================================================
// Helpers
// ============================================================================

function getAppointmentStatusColor(status: string): string {
  switch (status) {
    case 'scheduled': return 'bg-blue-100 text-blue-700';
    case 'confirmed': return 'bg-emerald-100 text-emerald-700';
    case 'checked_in': return 'bg-purple-100 text-purple-700';
    case 'in_progress': return 'bg-amber-100 text-amber-700';
    case 'completed': return 'bg-slate-100 text-slate-500';
    case 'no_show': return 'bg-red-100 text-red-600';
    case 'cancelled': return 'bg-slate-100 text-slate-400';
    default: return 'bg-slate-100 text-slate-600';
  }
}

function formatTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return '';
  }
}

function getToday(): { startDate: string; endDate: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

// ============================================================================
// Component
// ============================================================================

const MyDayDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clinic } = useClinic();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<EmrAppointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Determine the base path for navigation (same portal context)
  const basePath = useMemo(() => {
    if (location.pathname.startsWith('/practitioner-portal')) return '/practitioner-portal';
    if (location.pathname.startsWith('/clinic-staff-portal')) return '/clinic-staff-portal';
    return '';
  }, [location.pathname]);

  const loadTodayData = async (showRefresh = false) => {
    if (!clinic?.id) return;
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const { startDate, endDate } = getToday();
      const todayAppts = await emrService.getAppointments(clinic.id, {
        providerId: user?.practitionerId || undefined,
        startDate,
        endDate,
      });

      // Sort chronologically
      const sorted = (todayAppts || []).sort(
        (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      );

      setAppointments(sorted);
    } catch (error) {
      console.error('[MyDay] Failed to load schedule:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadTodayData();
  }, [clinic?.id, user?.practitionerId]);

  const handlePatientClick = (appt: EmrAppointment) => {
    const pid = appt.patient_id || appt.patient?.id;
    if (pid) {
      navigate(`${basePath}/patients/${pid}`);
    }
  };

  const now = new Date();
  const upcomingCount = appointments.filter(
    (a) => new Date(a.start_time) > now && a.status !== 'cancelled'
  ).length;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-montserrat text-slate-900 tracking-tight text-lg sm:text-2xl font-bold">
            My Day
          </h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
            {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            {upcomingCount > 0 && (
              <span className="ml-2 text-teal-600 font-medium">
                · {upcomingCount} upcoming
              </span>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadTodayData(true)}
          disabled={isRefreshing}
          className="border-slate-200"
        >
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1', isRefreshing && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Main grid: Schedule (60%) + Cards (40%) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* ──── Left: Today's Schedule ──── */}
        <div className="lg:col-span-3">
          <Card className="border border-slate-200">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-teal-500" />
                Today's Schedule
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`${basePath}/emr/calendar`)}
                className="text-slate-500 hover:text-slate-700"
              >
                Full Calendar <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              {appointments.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No appointments today</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => navigate(`${basePath}/emr/calendar`)}
                  >
                    View Calendar
                  </Button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {appointments.map((appt) => {
                    const patientName = appt.patient_name
                      || (appt.patient ? `${appt.patient.first_name} ${appt.patient.last_name}` : 'Unknown');
                    const isPast = new Date(appt.end_time) < now;
                    const isNow = new Date(appt.start_time) <= now && new Date(appt.end_time) >= now;

                    return (
                      <div
                        key={appt.id}
                        onClick={() => handlePatientClick(appt)}
                        className={cn(
                          'flex items-center gap-3 rounded-lg p-3 cursor-pointer transition-colors',
                          isNow
                            ? 'bg-teal-50 border border-teal-200'
                            : isPast
                              ? 'bg-slate-50 opacity-60 hover:opacity-80'
                              : 'hover:bg-slate-50 border border-transparent hover:border-slate-200'
                        )}
                      >
                        {/* Time column */}
                        <div className="flex-shrink-0 w-16 text-right">
                          <span className={cn(
                            'text-sm font-medium',
                            isNow ? 'text-teal-700' : isPast ? 'text-slate-400' : 'text-slate-700'
                          )}>
                            {formatTime(appt.start_time)}
                          </span>
                          <div className="text-[10px] text-slate-400">{appt.duration}min</div>
                        </div>

                        {/* Divider dot */}
                        <div className={cn(
                          'h-2 w-2 rounded-full flex-shrink-0',
                          isNow ? 'bg-teal-500' : isPast ? 'bg-slate-300' : 'bg-blue-400'
                        )} />

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900 truncate">
                              {toTitleCase(patientName)}
                            </span>
                            {appt.is_telehealth && (
                              <Video className="h-3 w-3 text-blue-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {appt.serviceType?.name && (
                              <span className="text-xs text-slate-500">{appt.serviceType.name}</span>
                            )}
                            {appt.title && !appt.serviceType?.name && (
                              <span className="text-xs text-slate-500">{appt.title}</span>
                            )}
                          </div>
                        </div>

                        {/* Status badge */}
                        <Badge className={cn('text-[10px] flex-shrink-0', getAppointmentStatusColor(appt.status))}>
                          {appt.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ──── Right: Action cards ──── */}
        <div className="lg:col-span-2 space-y-4">
          {/* Pending Rx */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Pill className="w-4 h-4 text-amber-500" />
                Pending Prescriptions
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`${basePath}/prescriptions`)}
                className="text-xs text-slate-400 hover:text-slate-600 h-7 px-2"
              >
                View <ArrowRight className="w-3 h-3 ml-0.5" />
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50">
                <Clock className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Review prescriptions queue</p>
                  <p className="text-xs text-slate-500 mt-0.5">Check for orders awaiting signature</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* IntelliHealth Alerts */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-500" />
                IntelliHealth Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50">
                <AlertTriangle className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Patients needing attention</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    CDS recommendations load per-patient on chart open
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Unread Messages */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-violet-500" />
                Messages
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`${basePath}/messaging`)}
                className="text-xs text-slate-400 hover:text-slate-600 h-7 px-2"
              >
                View <ArrowRight className="w-3 h-3 ml-0.5" />
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-violet-50">
                <MessageSquare className="w-5 h-5 text-violet-500" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Patient messages</p>
                  <p className="text-xs text-slate-500 mt-0.5">Review and respond to patient messages</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-orange-500" />
                Tasks
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`${basePath}/tasks`)}
                className="text-xs text-slate-400 hover:text-slate-600 h-7 px-2"
              >
                View <ArrowRight className="w-3 h-3 ml-0.5" />
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              {user?.practitionerId ? (
                <TaskPanel providerId={user.practitionerId} clinicId={clinic?.id} />
              ) : (
                <p className="text-xs text-slate-400 py-3 text-center">
                  No provider profile linked
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MyDayDashboard;
